import { formatMoney } from '@belong/common';
import { IconInfo } from '@belong/icons';
import { Tooltip, Money } from '@belong/ui';
import { MaintenanceModel, ReportDetailModel } from 'api/models';
import classNames from 'classnames/bind';
import Collapsible from 'components/Collapsible/Collapsible';
import NameValueTable from 'components/NameValueTable/NameValueTable';
import { BackedByBelong } from 'containercomponents/MaintenanceStatus/MaintenanceCostDisplay/MaintenanceCostDisplay';
import { SplitInfoBox } from 'containercomponents/MaintenanceStatus/split-infobox/split-infobox';
import Space from 'corecomponents/Space/Space';
import { Flex, Box, Text } from 'design-system';
import { useModal } from 'hooks/useModal';
import { MaintenanceRequester } from 'models/enums';
import PriceTable, {
  getHomeownerBundlePriceSection,
} from 'pages/PostInspectionFlow/steps/Improvements/Approval/PriceTable/PriceTable';
import { formatString } from 'strings';
import styles from './GroupHomeownerReceiptCosts.module.css';

const cx = classNames.bind(styles);

type GroupHomeownerReceiptCostsProps = {
  groupTask: MaintenanceModel;
  report?: ReportDetailModel;
};

export const GroupHomeownerReceiptCosts = ({
  groupTask,
  report,
}: GroupHomeownerReceiptCostsProps) => {
  const [isModalOpen, setModalOpen, setModalClose] = useModal();

  const homeownerPayment = report?.details?.group?.payments?.find(
    (p: any) => p.paidBy === MaintenanceRequester.Homeowner,
  );

  if (!homeownerPayment || !groupTask) return null;

  // Getting past the guard means the optional chain above resolved, so every
  // link in it exists; this names the group once instead of re-asserting it.
  const group = report!.details!.group!;
  const groupPayments = group.payments!;

  const residentPayment = groupPayments.find(
    (p: any) => p.paidBy === MaintenanceRequester.Resident,
  );
  const belongPayment = groupPayments.find(
    (p: any) => p.paidBy === MaintenanceRequester.Belong,
  );

  const isBilledByGroup = group.isDoNotExceedGroup && group.isLegacyInvoicing;
  const isInvoiced =
    group.requestTypeDetails!.hasPublishedInvoices && !isBilledByGroup;
  const { isLegacyInvoicing, financedPayment, result, payments, priceRange } =
    groupTask;

  const residentRange = priceRange?.priceRangeBreakdown?.find(
    (range) => range.paidBy === 'Resident',
  );
  const belongRange = priceRange?.priceRangeBreakdown?.find(
    (range) => range.paidBy === 'Belong',
  );

  // NOTE: the two `?.upperBound` reads below are a stale field name — the model
  // has `upperBoundSubtotal` and `upperBoundTotal`, not `upperBound` — so they
  // have always evaluated to `undefined` and each of those conditions has been
  // decided by its payment check alone. Kept as-is because picking the
  // replacement is a pricing decision; see §3 of
  // docs/development/typecheck-baseline-followups.md.

  const isMonthly = homeownerPayment.paymentPlan === 'Monthly';

  const showMonthlyAmount =
    !isInvoiced && isMonthly && homeownerPayment.installmentCount;

  const isImprovementsReport =
    ['HomeOnboarding', 'MoveOutRepairs'].includes(groupTask.requestCategory!) &&
    ['Improvements', 'Move Out Repairs'].includes(groupTask?.summary);

  const isMoveOut = groupTask?.requestCategory === 'MoveOutRepairs';

  const hideItemsCost = !!(
    isImprovementsReport && groupTask?.isDoNotExceedGroup
  );

  const bundleSections = getHomeownerBundlePriceSection({
    bundles: report!.details!.bundles,
    priceRange: null,
    isInvoiced,
    hideItemsCost,
    inspectionType: {
      isOnboarding: groupTask?.requestCategory === 'HomeOnboarding',
      isMoveOut,
    },
    onChange: null,
  });

  const groupPayment = payments!.find(
    (p) => p.paidBy === MaintenanceRequester.Homeowner,
  );
  const financePlanId = groupPayment?.balances?.find(
    (balance) => !!balance.currentFinanceId,
  )?.currentFinanceId;
  const hasElegibleOpenBalance = !!groupPayment?.balances?.find(
    (balance) => balance.isOpen && balance.isFinanceable,
  );

  // `as const` keeps these literal keys, so indexing the payment model with them
  // below stays checked. Only the non-invoiced shape carries `installmentEach`.
  const costs = isInvoiced
    ? ({
        subtotal: 'actualAdjustmentApplied',
        discount: 'actualDiscountAmount',
        netTotal: 'actualNetTotal',
      } as const)
    : ({
        subtotal: 'estimatedAdjustmentApplied',
        discount: 'estimatedDiscountAmount',
        netTotal: 'estimatedDiscountApplied',
        installmentEach: 'estimatedInstallmentEach',
      } as const);

  const homeownerSubtotal =
    homeownerPayment[costs.subtotal] +
    (residentPayment?.[costs.subtotal] || 0) +
    (belongPayment?.[costs.subtotal] || 0);

  const tooltip = (
    <Tooltip
      content={
        homeownerPayment.installmentCount === 1
          ? '1% Fee ($20 Minimum)'
          : '1% Fee/Month ($20 Minimum)'
      }
    />
  );

  const isSubtotalVisible =
    homeownerPayment[costs.discount] > 0 ||
    report?.report?.subType !== 'ImprovementsRepairsInspection';

  const isDueUpfrontCostVisible =
    !groupTask?.isDoNotExceedGroup &&
    (financedPayment?.dueNow ?? 0) > 0 &&
    !isInvoiced;

  const isDueNowVisible =
    !isInvoiced &&
    homeownerPayment.estimatedUpfrontAmount > 0 &&
    !groupTask?.isDoNotExceedGroup;

  const neverExceedCopy = (
    <div className="flex">
      <span className="mr-2xs">Never Exceed</span>

      <Tooltip
        contentProps={{ className: 'z-50' }}
        content={
          <div className="max-w-md rounded border-dark-navy bg-dark-navy p-2sm">
            <p className="p1 text-left font-regular text-white">
              We guarantee you&apos;ll never pay more than this approved amount.
              If costs exceed it, Belong will cover the difference.
            </p>
          </div>
        }
        arrowProps={{
          className: 'fill-dark-navy border-dark-navy',
        }}
      >
        <button
          className="flex cursor-pointer items-center fill-navy"
          type="button"
        >
          <IconInfo width={18} />
        </button>
      </Tooltip>
    </div>
  );

  const totalCostCopy = isInvoiced
    ? 'Subtotal'
    : groupTask?.isDoNotExceedGroup
      ? neverExceedCopy
      : 'Est. Total';

  const isExclusivityOptedIn =
    groupTask?.isDoNotExceedGroup && groupTask?.exclusivityOptedIn;

  const getHomeownerTotalCopy = () => {
    if (isExclusivityOptedIn) {
      return `Complimentary Repairs (${formatMoney(homeownerSubtotal, 'DOLLARS_NO_CENTS')} Credit)`;
    }

    if (groupTask?.isDoNotExceedGroup && !groupTask?.exclusivityOptedIn) {
      return neverExceedCopy;
    }

    if (isMoveOut) {
      return 'Your Estimated Expenses';
    }

    return 'Total';
  };

  const hoTotalCopy = getHomeownerTotalCopy();

  return (
    <Collapsible
      createMarginAndPaddingClassName={cx('margin-padding')}
      collapsed={false}
      title={isInvoiced ? 'Receipt' : 'Estimated Pricing'}
      inner={
        <div className={cx('content')}>
          <BackedByBelong
            showModal={isModalOpen}
            onClick={setModalOpen}
            onHide={setModalClose}
          />
          <Space />
          <div className={cx('maintenanceCostDisplay')}>
            <PriceTable
              nameHeader="Item"
              valueHeader="Amount"
              sections={[bundleSections]}
            />
            {isSubtotalVisible && (
              <>
                <div className={cx('underline')} />

                <NameValueTable
                  noBold
                  items={[
                    {
                      name: totalCostCopy,
                      value: <Money value={homeownerSubtotal} />,
                    },
                  ]}
                />

                {/* Deductions */}
                {!!homeownerPayment[costs.discount] && (
                  <NameValueTable
                    noBold
                    discountValue
                    items={[
                      {
                        name: 'Savings',
                        value: (
                          <span className="flex font-semibold">
                            -
                            <Money
                              format="DOLLARS_NO_CENTS"
                              value={homeownerPayment[costs.discount]}
                            />
                          </span>
                        ),
                      },
                    ]}
                  />
                )}
              </>
            )}
            {(residentPayment?.[costs.subtotal] > 0 ||
              (residentRange as any)?.upperBound > 0) && (
              <NameValueTable
                noBold
                discountValue
                items={[
                  {
                    name: 'Resident Responsibility',
                    value: (
                      <p className="text-navy">
                        -
                        <Money
                          as="span"
                          value={residentPayment[costs.subtotal]}
                        />
                      </p>
                    ),
                  },
                ]}
              />
            )}
            {(belongPayment?.[costs.subtotal] > 0 ||
              (belongRange as any)?.upperBound > 0) && (
              <NameValueTable
                noBold
                discountValue
                items={[
                  {
                    name: 'Belong Responsibility',
                    value: (
                      <p>
                        -
                        <Money
                          as="span"
                          value={belongPayment[costs.subtotal]}
                        />
                      </p>
                    ),
                  },
                ]}
              />
            )}

            {!isMoveOut && <div className={cx('underline')} />}

            {isLegacyInvoicing ? (
              <>
                <NameValueTable
                  noBold
                  items={[
                    {
                      name: hoTotalCopy,
                      value: isExclusivityOptedIn ? (
                        <p className="flex gap-2xs">
                          <span className="font-regular text-dark-gray line-through">
                            <Money
                              value={homeownerPayment[costs.netTotal]}
                              format="DOLLARS_NO_CENTS"
                            />
                          </span>
                          <Money value={0} format="DOLLARS_NO_CENTS" />
                        </p>
                      ) : (
                        <Money
                          value={homeownerPayment[costs.netTotal]}
                          format="DOLLARS_WITH_CENTS"
                        />
                      ),
                    },
                  ]}
                />
                {isInvoiced && isMonthly && (
                  <Flex justifyContent="flex-end">
                    <Text variant="body-responsive" mr="xs">
                      (w/ installment fees)
                    </Text>
                    {tooltip}
                  </Flex>
                )}
                {showMonthlyAmount && (
                  <>
                    <div className={cx('underline')} />
                    <NameValueTable
                      boldValue
                      items={[
                        {
                          name: (
                            <Flex>
                              <Box mr="xs">
                                {formatString(
                                  `With ${homeownerPayment.installmentCount}-Month <span>Payment Plan</span>`,
                                )}
                              </Box>
                              {tooltip}
                            </Flex>
                          ),
                          value: (
                            <>
                              <Money
                                format="DOLLARS_NO_CENTS"
                                // Only reachable when `showMonthlyAmount` is
                                // set, which requires `!isInvoiced`.
                                value={homeownerPayment[costs.installmentEach!]}
                              />
                              /Mo.
                            </>
                          ),
                        },
                      ]}
                    />
                  </>
                )}
                {isDueNowVisible && (
                  <NameValueTable
                    boldValue
                    items={[
                      {
                        name: (
                          <Flex>
                            <Box mr="xs">Due Now</Box>
                          </Flex>
                        ),
                        value: (
                          <>
                            <Money
                              format="DOLLARS_NO_CENTS"
                              value={homeownerPayment.estimatedUpfrontAmount}
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                )}
              </>
            ) : (
              <>
                <NameValueTable
                  boldValue
                  items={[
                    {
                      name: totalCostCopy,
                      value: <Money value={homeownerPayment[costs.netTotal]} />,
                    },
                  ]}
                />
                {isDueUpfrontCostVisible && (
                  <NameValueTable
                    boldValue
                    items={[
                      {
                        name: (
                          <Flex>
                            <Box mr="xs">Due Upfront</Box>
                          </Flex>
                        ),
                        value: (
                          <>
                            <Money
                              format="DOLLARS_NO_CENTS"
                              value={financedPayment?.dueNow}
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                )}
                {!hideItemsCost && (
                  <div className="mt-sm">
                    <SplitInfoBox
                      financePlanId={financePlanId}
                      hasOpenBalance={hasElegibleOpenBalance}
                      isMaintenanceCompleted={result === 'Completed'}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      }
    />
  );
};
